import React, { useState, useCallback } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import "./notes.css";
import CGFundamentals from "./CGFundamentals";
import RaytracingIntro from "./RaytracingIntro";
import RaySlab from "./RaySlab";
import SurfaceShading from "./SurfaceShading";
import Transformations from "./Transformations";
import SceneGraph from "./SceneGraph";
import RecursiveRayTracing from "./RecursiveRayTracing";
import Radiometry from "./Radiometry";

const lectures = [
  {
    slug: "cg-fundamentals",
    title: "CG Fundamentals – Part I",
    desc: "Scalars, points, vectors, dot & cross products, Euclidean spaces, frames, handedness.",
  },
  {
    slug: "raytracing-intro",
    title: "Introduction to Ray Tracing",
    desc: "Rendering approaches, ray generation, camera setup, FOV, and sphere/plane/triangle/AABB intersections.",
  },
  {
    slug: "ray-slab",
    title: "Ray Slab – In Depth",
    desc: "Deep dive on the AABB slab intersection method with worked examples and Quake II source analysis.",
  },
  {
    slug: "surface-shading",
    title: "Surface Shading",
    desc: "Surface normals, diffuse/Lambertian shading, ambient illumination, Phong, reflection vector derivation, Blinn-Phong, multiple lights, and distance attenuation.",
  },
  {
    slug: "transformations",
    title: "Transformations",
    desc: "2D/3D scale, shear, rotation, translation, homogeneous coordinates, compound transforms, Euler angles, quaternions, and inverse transformations.",
  },
  {
    slug: "scene-graph",
    title: "Scene Graphs & Matrix Stacks",
    desc: "DAG-based hierarchical scenes, local-to-world transforms, matrix stack push/pop, inverse transforms, declarative graphs, and cross-level coordinate changes.",
  },
  {
    slug: "recursive-ray-tracing",
    title: "Recursive Ray Tracing",
    desc: "Shadow rays, reflections, refractions, Snell's law, total internal reflection, Fresnel effect, and the Schlick approximation.",
  },
  {
    slug: "radiometry",
    title: "Radiometry",
    desc: "Photons, radiant flux, solid angles, radiance, irradiance, reflectance, BRDF, material types, and radiosity.",
  },
];

const BASE = "/omscs/computer-graphics";

/* ── Index page ─────────────────────────────────────────────── */
const Index = () => (
  <div className="notes-content">
    <Link to="/" className="back-link">← Carlos Trejo</Link>
    <h1>OMSCS — Computer Graphics</h1>
    <p className="subtitle">Georgia Institute of Technology</p>
    <p>
      Notes from the CS 6491 Computer Graphics course covering prerequisite
      math, ray tracing fundamentals, and intersection algorithms.
    </p>
    <div className="lecture-cards">
      {lectures.map((l, i) => (
        <Link key={l.slug} to={`${BASE}/${l.slug}`} className="lecture-card">
          <div className="card-num">Lecture {i + 1}</div>
          <div className="card-title">{l.title}</div>
          <div className="card-desc">{l.desc}</div>
        </Link>
      ))}
    </div>
  </div>
);

/* ── Lecture wrapper (sidebar + content) ────────────────────── */
const LectureWrapper = ({ slug, Component }) => {
  const [toc, setToc] = useState([]);
  const renderToc = useCallback((t) => setToc(t), []);

  return (
    <div className="notes-layout">
      {/* Sidebar */}
      <nav className="notes-sidebar">
        <Link to={BASE} className="sidebar-brand">← All Lectures</Link>

        <div className="sidebar-section">Lectures</div>
        {lectures.map((l) => (
          <Link
            key={l.slug}
            to={`${BASE}/${l.slug}`}
            className={`nav-lecture${slug === l.slug ? " active" : ""}`}
          >
            {l.title}
          </Link>
        ))}

        {toc.length > 0 && (
          <>
            <div className="sidebar-section" style={{ marginTop: "0.75rem" }}>
              Contents
            </div>
            {toc.map(([label, anchor]) => (
              <a key={anchor} href={`#${anchor}`} className="nav-toc">
                {label}
              </a>
            ))}
          </>
        )}
      </nav>

      {/* Main content */}
      <main className="notes-content">
        <Link to="/" className="back-link">← Carlos Trejo</Link>
        <Component renderToc={renderToc} />
      </main>
    </div>
  );
};

/* ── Root component ─────────────────────────────────────────── */
const ComputerGraphics = () => {
  return (
    <Routes>
      <Route index element={
        <div className="notes-layout">
          <nav className="notes-sidebar">
            <Link to={BASE} className="sidebar-brand">CG Notes</Link>
            <div className="sidebar-section">Lectures</div>
            {lectures.map((l) => (
              <Link key={l.slug} to={`${BASE}/${l.slug}`} className="nav-lecture">
                {l.title}
              </Link>
            ))}
          </nav>
          <Index />
        </div>
      } />
      <Route
        path="cg-fundamentals"
        element={<LectureWrapper slug="cg-fundamentals" Component={CGFundamentals} />}
      />
      <Route
        path="raytracing-intro"
        element={<LectureWrapper slug="raytracing-intro" Component={RaytracingIntro} />}
      />
      <Route
        path="ray-slab"
        element={<LectureWrapper slug="ray-slab" Component={RaySlab} />}
      />
      <Route
        path="surface-shading"
        element={<LectureWrapper slug="surface-shading" Component={SurfaceShading} />}
      />
      <Route
        path="transformations"
        element={<LectureWrapper slug="transformations" Component={Transformations} />}
      />
      <Route
        path="scene-graph"
        element={<LectureWrapper slug="scene-graph" Component={SceneGraph} />}
      />
      <Route
        path="recursive-ray-tracing"
        element={<LectureWrapper slug="recursive-ray-tracing" Component={RecursiveRayTracing} />}
      />
      <Route
        path="radiometry"
        element={<LectureWrapper slug="radiometry" Component={Radiometry} />}
      />
      <Route path="*" element={<Navigate to={BASE} replace />} />
    </Routes>
  );
};

export default ComputerGraphics;
